import React, { useState } from 'react';
import { STATE_REACH } from '@/data/seed';
import { fmtIN, shortIN } from '@/lib/fmt';
import { MapPin } from 'lucide-react';
import IndiaChoropleth from '@/components/IndiaChoropleth';

export default function Geography() {
  const [hovered, setHovered] = useState(null);
  const sorted = [...STATE_REACH].sort((a, b) => b.beneficiaries - a.beneficiaries);
  const totalStates = STATE_REACH.length;
  const totalProjects = STATE_REACH.reduce((s, x) => s + x.projects, 0);
  const totalDistricts = STATE_REACH.reduce((s, x) => s + x.districts, 0);
  const totalBen = STATE_REACH.reduce((s, x) => s + x.beneficiaries, 0);
  const selected = hovered ? STATE_REACH.find((s) => s.state === hovered) : null;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Geographic footprint</div>
          <h1>Geography</h1>
          <div className="sub">Where we live, work and walk — {totalStates} states, {totalDistricts} districts, {fmtIN(totalBen)} lives</div>
        </div>
      </div>

      <div className="card map-card" style={{ padding: 0, display: 'grid', gridTemplateColumns: '2fr 1fr' }}>
        <IndiaChoropleth
          stateReach={STATE_REACH}
          hovered={hovered}
          onHover={setHovered}
          height={520}
        />

        <div className="border-l border-[color:var(--gray-100)] p-5 bg-[color:var(--gray-50)]">
          {selected ? (
            <>
              <div className="text-[18px] font-extrabold text-[color:var(--gray-900)] tracking-tight">{selected.state}</div>
              <div className="text-[11.5px] text-[color:var(--gray-400)] mb-3.5">Hover-locked</div>
              <div className="grid grid-cols-2 gap-2.5 mb-3.5">
                <Item l="Beneficiaries" v={fmtIN(selected.beneficiaries)} />
                <Item l="Projects" v={selected.projects} />
                <Item l="Districts" v={selected.districts} />
                <Item l="Share of India" v={((selected.beneficiaries / totalBen) * 100).toFixed(1) + '%'} />
              </div>
            </>
          ) : (
            <>
              <div className="text-[18px] font-extrabold text-[color:var(--gray-900)] tracking-tight">Pan-India footprint</div>
              <div className="text-[11.5px] text-[color:var(--gray-400)] mb-3.5">Hover a tile or a row for state details.</div>
              <div className="grid grid-cols-2 gap-2.5 mb-3.5">
                <Item l="States reached" v={totalStates} />
                <Item l="Districts" v={totalDistricts} />
                <Item l="Projects" v={totalProjects + '+'} />
                <Item l="Beneficiaries" v={shortIN(totalBen)} />
              </div>
            </>
          )}

          <div className="mt-4 pt-3 border-t border-[color:var(--gray-200)]">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-[color:var(--gray-500)] mb-2">
              Ranked by beneficiaries
            </div>
            <ol className="max-h-[220px] overflow-y-auto">
              {sorted.map((s, i) => {
                const isOn = hovered === s.state;
                return (
                  <li key={s.state}
                    onMouseEnter={() => setHovered(s.state)}
                    onMouseLeave={() => setHovered(null)}
                    className={`flex items-center justify-between py-1.5 text-[12.5px] cursor-default transition-colors ${
                      isOn ? 'bg-[color:var(--llf-gold-soft)] rounded px-2 -mx-2' : ''
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-[10px] tabular-nums text-[color:var(--gray-400)] w-5">{String(i + 1).padStart(2, '0')}</span>
                      <MapPin size={11} className="text-[color:var(--gray-400)]" />
                      <span className="text-[color:var(--gray-800)]">{s.state}</span>
                    </span>
                    <span className="tabular-nums font-semibold text-[color:var(--gray-900)]">{shortIN(s.beneficiaries)}</span>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <div className="card-title">State × Program footprint (top 8 states)</div>
            <div className="card-sub">Projects per state, split by pillar (illustrative distribution)</div>
          </div>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr><th>State</th><th className="num">Beneficiaries</th><th className="num">Projects</th><th className="num">Districts</th><th className="num">Share</th></tr>
            </thead>
            <tbody>
              {sorted.slice(0, 10).map((s) => (
                <tr key={s.state}>
                  <td><span className="ent">{s.state}</span></td>
                  <td className="num">{fmtIN(s.beneficiaries)}</td>
                  <td className="num">{s.projects}</td>
                  <td className="num">{s.districts}</td>
                  <td className="num">{((s.beneficiaries / totalBen) * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

const Item = ({ l, v }) => (
  <div>
    <div className="text-[10.5px] uppercase tracking-wider text-[color:var(--gray-500)] font-medium">{l}</div>
    <div className="text-[17px] font-bold text-[color:var(--gray-900)] tabular-nums mt-0.5">{v}</div>
  </div>
);
